import { forwardRef } from "react";
import { Search, Mic, FileText, Clock, Plus, Scan, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";

export type PosTopBarProps = {
  query: string;
  onQueryChange: (q: string) => void;
  onNewSale: () => void;
  onHoldBill: () => void;
  onRecentSales: () => void;
  scannerMode: "usb" | "camera";
  onOpenCameraScan: () => void;
};

export const PosTopBar = forwardRef<HTMLInputElement, PosTopBarProps>(function PosTopBar(
  {
    query,
    onQueryChange,
    onNewSale,
    onHoldBill,
    onRecentSales,
    scannerMode,
    onOpenCameraScan,
  },
  ref,
) {
  return (
    <div className="border-b border-border bg-card px-3 py-2 sm:px-4">
      <div className="mx-auto flex max-w-screen-2xl items-center gap-2 sm:gap-3">
        <div className="min-w-0 flex-1 lg:mx-auto lg:max-w-2xl">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={ref}
              type="text"
              placeholder="Scan barcode or search product…"
              className="w-full min-h-9 rounded-xl border-none bg-input-background py-2.5 pl-10 pr-10 text-sm text-foreground outline-none transition-all focus:ring-2 focus:ring-primary [@media(pointer:coarse)]:min-h-10"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-muted-foreground hover:text-primary"
              aria-label="Voice search"
              onClick={() => toast.message("Voice search is not available yet")}
            >
              <Mic className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2 sm:ml-auto lg:ml-0">
          {scannerMode === "camera" ? (
            <button
              type="button"
              onClick={onOpenCameraScan}
              className="flex items-center gap-1.5 rounded-xl bg-muted px-2.5 py-2 text-foreground hover:bg-accent"
            >
              <Scan className="h-4 w-4" />
              <span className="hidden md:inline">Scan</span>
            </button>
          ) : null}
          <button
            type="button"
            onClick={onNewSale}
            className="flex items-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">New Sale</span>
          </button>
          <button
            type="button"
            onClick={onHoldBill}
            className="hidden items-center gap-1.5 rounded-xl bg-muted px-3 py-2 text-foreground hover:bg-accent sm:flex"
          >
            <FileText className="h-4 w-4" />
            <span className="hidden md:inline">Hold Bill</span>
          </button>
          <button
            type="button"
            onClick={onRecentSales}
            className="hidden items-center gap-1.5 rounded-xl bg-muted px-3 py-2 text-foreground hover:bg-accent sm:flex"
          >
            <Clock className="h-4 w-4" />
            <span className="hidden md:inline">Recent</span>
          </button>
          <details className="relative sm:hidden">
            <summary className="flex list-none cursor-pointer items-center rounded-xl bg-muted p-2 text-foreground hover:bg-accent">
              <MoreHorizontal className="h-4 w-4" />
            </summary>
            <div className="absolute right-0 top-10 z-20 w-36 rounded-lg border border-border bg-popover p-1 shadow-md">
              <button
                type="button"
                onClick={onHoldBill}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
              >
                <FileText className="h-4 w-4" />
                Hold Bill
              </button>
              <button
                type="button"
                onClick={onRecentSales}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
              >
                <Clock className="h-4 w-4" />
                Recent
              </button>
            </div>
          </details>
        </div>
      </div>
    </div>
  );
});
